import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2 } from "lucide-react";

const ALL_PERMS = [
  { id: "internships", label: "Manage Internships", desc: "Create, edit, review applications & upload certificates." },
  { id: "tests", label: "Manage Test Series & Exams", desc: "Create tests, review attempts, declare results." },
  { id: "payments", label: "View Payments", desc: "Access payment records and revenue data." },
  { id: "gallery", label: "Manage Gallery", desc: "Add/remove photos from the public gallery." },
];

export default function VolunteerDashboard() {
  const { user } = useAuth();
  const perms = user?.permissions || [];

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Volunteer</div>
        <h1 className="heading text-3xl font-bold mt-1">Welcome, {user?.name?.split(" ")[0]} 💙</h1>
        <p className="text-slate-500 mt-1">Thank you for being part of our mission.</p>
      </div>
      <div className="p-5 rounded-xl border border-slate-200 bg-white">
        <h2 className="heading text-lg font-bold">Your Permissions</h2>
        <p className="text-xs text-slate-500 mt-1">Admin has assigned the following capabilities to you.</p>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {ALL_PERMS.map((p) => {
            const has = perms.includes(p.id);
            return (
              <div key={p.id} className={`p-4 rounded-xl border-2 ${has ? "border-brand bg-blue-50" : "border-slate-200 bg-slate-50 opacity-60"}`} data-testid={`vol-perm-${p.id}`}>
                <div className="flex items-start gap-2">
                  <CheckCircle2 size={18} className={has ? "text-brand" : "text-slate-300"} />
                  <div>
                    <div className="font-bold">{p.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.desc}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {perms.length === 0 && (
          <div className="mt-4 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
            You have no permissions assigned yet. Please wait for Admin to grant access.
          </div>
        )}
      </div>
      {perms.length > 0 && (
        <div className="mt-6 p-5 rounded-xl border border-slate-200 bg-white">
          <h2 className="heading text-lg font-bold">Quick Access</h2>
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {perms.includes("internships") && <QuickLink href="/admin/internships" label="Internships" />}
            {perms.includes("internships") && <QuickLink href="/admin/applications" label="Applications" />}
            {perms.includes("tests") && <QuickLink href="/admin/test-series" label="Test Series" />}
            {perms.includes("tests") && <QuickLink href="/admin/attempts" label="Exam Attempts" />}
            {perms.includes("payments") && <QuickLink href="/admin/payments" label="Payments" />}
            {perms.includes("gallery") && <QuickLink href="/admin/gallery" label="Gallery" />}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function QuickLink({ href, label }) {
  return (
    <a href={href} className="p-4 rounded-xl border border-slate-200 bg-white hover:border-brand hover:-translate-y-0.5 transition text-center font-semibold text-sm">
      {label} →
    </a>
  );
}
